/**
 * Admin Panel
 *
 * Shows all users and their character assignments.
 * Only accessible to admin users (brottler@gmail.com).
 */

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { GouldCorner } from '../Ornaments';
import { Icon } from '../Icon';
import { BetaKeysPanel } from './BetaKeysPanel';

type AdminTab = 'users' | 'betaKeys';

// Admin email whitelist
const ADMIN_EMAILS = ['brottler@gmail.com'];

interface MachinaUser {
  id: string;
  display_name: string;
  email?: string;
  created_at?: string;
}

interface MachinaCharacter {
  id: string;
  user_id: string;
  campaign_id: string | null;
  data: {
    name: string;
    race?: string;
    playerName?: string;
    classes?: Array<{ name: string; level: number; subclass?: string }>;
  };
  created_at?: string;
}

interface AdminPanelProps {
  onClose: () => void;
}

export function isAdmin(email: string | undefined): boolean {
  return email ? ADMIN_EMAILS.includes(email.toLowerCase()) : false;
}

export function AdminPanel({ onClose }: AdminPanelProps) {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<MachinaUser[]>([]);
  const [characters, setCharacters] = useState<MachinaCharacter[]>([]);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState<string | null>(null);
  const [changingCampaign, setChangingCampaign] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Verify admin access
  if (!isAdmin(user?.email)) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 style={styles.title}>Access Denied</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            You do not have admin privileges.
          </p>
          <button onClick={onClose} style={styles.closeButton}>Close</button>
        </div>
      </div>
    );
  }

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    setError(null);

    try {
      // Load users - join with auth.users to get email
      const { data: usersData, error: usersError } = await supabase
        .from('machina_users')
        .select('*')
        .order('display_name');

      if (usersError) throw usersError;

      // Load all characters
      const { data: charsData, error: charsError } = await supabase
        .from('machina_characters')
        .select('*')
        .order('created_at');

      if (charsError) throw charsError;

      // Load campaigns for reference
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('machina_campaigns')
        .select('id, name')
        .order('name');

      if (campaignsError) throw campaignsError;

      setUsers(usersData || []);
      setCharacters(charsData || []);
      setCampaigns(campaignsData || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReassignCharacter(characterId: string, newUserId: string) {
    if (!newUserId) return;

    setReassigning(characterId);
    try {
      // Get the character to check its campaign
      const char = characters.find((c) => c.id === characterId);

      // Get the new user's display name to update playerName
      const newUser = users.find((u) => u.id === newUserId);
      const newPlayerName = newUser?.display_name || 'Player';

      // Update both user_id and playerName in the data field
      const updatedData = char?.data ? { ...char.data, playerName: newPlayerName } : { playerName: newPlayerName };

      const { error } = await supabase
        .from('machina_characters')
        .update({ user_id: newUserId, data: updatedData })
        .eq('id', characterId);

      if (error) throw error;

      // If character has a campaign, ensure new user is a campaign member
      if (char?.campaign_id) {
        await ensureCampaignMember(char.campaign_id, newUserId);
      }

      // Refresh data
      await loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReassigning(null);
      setSelectedUserId('');
    }
  }

  async function handleChangeCampaign(characterId: string, newCampaignId: string | null) {
    setChangingCampaign(characterId);
    try {
      // Get the character to know the user
      const char = characters.find((c) => c.id === characterId);

      // Update both the campaign_id column and the campaignId inside the data JSON
      const updatedData = char?.data ? { ...char.data, campaignId: newCampaignId || undefined } : {};

      const { error } = await supabase
        .from('machina_characters')
        .update({ campaign_id: newCampaignId, data: updatedData })
        .eq('id', characterId);

      if (error) throw error;

      // If assigning to a campaign, ensure user is a campaign member
      if (newCampaignId && char?.user_id) {
        await ensureCampaignMember(newCampaignId, char.user_id);
      }

      // Refresh data
      await loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setChangingCampaign(null);
    }
  }

  async function ensureCampaignMember(campaignId: string, userId: string) {
    // Check if already a member
    const { data: existing } = await supabase
      .from('machina_campaign_members')
      .select('campaign_id')
      .eq('campaign_id', campaignId)
      .eq('user_id', userId)
      .single();

    if (!existing) {
      // Add as player member
      const { error } = await supabase
        .from('machina_campaign_members')
        .insert({ campaign_id: campaignId, user_id: userId, role: 'player' });

      if (error && !error.message.includes('duplicate')) {
        console.error('Failed to add campaign member:', error);
      }
    }
  }

  function getCharactersForUser(userId: string) {
    return characters.filter((c) => c.user_id === userId);
  }

  function formatCharacterInfo(char: MachinaCharacter) {
    const data = char.data;
    const classInfo = data.classes?.map((c) => `${c.name} ${c.level}`).join('/') || 'Unknown';
    return `${data.name} (${data.race || 'Unknown'} ${classInfo})`;
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <GouldCorner size={20} style={{ position: 'absolute', top: 4, left: 4 }} />
          <GouldCorner size={20} style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)' }} />

          <h2 style={styles.title}>
            <Icon name="crown" size={24} color="#d4af37" />
            Admin Panel
          </h2>

          <button onClick={onClose} style={styles.closeIconButton}>
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          <button
            onClick={() => setActiveTab('users')}
            style={activeTab === 'users' ? styles.tabActive : styles.tab}
          >
            <Icon name="users" size={14} />
            Users
          </button>
          <button
            onClick={() => setActiveTab('betaKeys')}
            style={activeTab === 'betaKeys' ? styles.tabActive : styles.tab}
          >
            <Icon name="lock" size={14} />
            Beta Keys
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'betaKeys' && <BetaKeysPanel />}

          {activeTab === 'users' && (
            <>
              {isLoading && (
                <div style={styles.loading}>
                  <Icon name="loader" size={24} />
                  Loading users and characters...
                </div>
              )}

              {error && (
                <div style={styles.error}>
                  <Icon name="alert" size={16} />
                  {error}
                </div>
              )}

              {!isLoading && !error && (
                <>
                  {/* Stats summary */}
                  <div style={styles.stats}>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{users.length}</span>
                      <span style={styles.statLabel}>Users</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{characters.length}</span>
                      <span style={styles.statLabel}>Characters</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{campaigns.length}</span>
                      <span style={styles.statLabel}>Campaigns</span>
                    </div>
                  </div>

                  {/* Users list */}
                  <div style={styles.usersList}>
                    {users.map((u) => {
                      const userChars = getCharactersForUser(u.id);
                      return (
                        <div key={u.id} style={styles.userCard}>
                          <div style={styles.userHeader}>
                            <div style={styles.userInfo}>
                              <span style={styles.userName}>{u.display_name}</span>
                              <span style={styles.userId}>ID: {u.id.slice(0, 8)}...</span>
                            </div>
                            <span style={styles.charCount}>
                              {userChars.length} character{userChars.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {userChars.length > 0 && (
                            <div style={styles.charList}>
                              {userChars.map((char) => (
                                <div key={char.id} style={styles.charItem}>
                                  <div style={styles.charInfo}>
                                    <span style={styles.charName}>{formatCharacterInfo(char)}</span>
                                  </div>

                                  {/* Campaign dropdown */}
                                  <div style={styles.reassignSection}>
                                    <select
                                      value={char.campaign_id || ''}
                                      onChange={(e) => {
                                        const newCampaignId = e.target.value || null;
                                        handleChangeCampaign(char.id, newCampaignId);
                                      }}
                                      disabled={changingCampaign === char.id}
                                      style={styles.selectCampaign}
                                    >
                                      <option value="">No Campaign</option>
                                      {campaigns.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                    {changingCampaign === char.id && (
                                      <Icon name="loader" size={14} style={{ marginLeft: 4 }} />
                                    )}
                                  </div>

                                  {/* Reassign user dropdown */}
                                  <div style={styles.reassignSection}>
                                    <select
                                      value={reassigning === char.id ? selectedUserId : ''}
                                      onChange={(e) => {
                                        setSelectedUserId(e.target.value);
                                        if (e.target.value) {
                                          handleReassignCharacter(char.id, e.target.value);
                                        }
                                      }}
                                      disabled={reassigning === char.id}
                                      style={styles.select}
                                    >
                                      <option value="">Reassign to...</option>
                                      {users.filter((other) => other.id !== u.id).map((other) => (
                                        <option key={other.id} value={other.id}>
                                          {other.display_name}
                                        </option>
                                      ))}
                                    </select>
                                    {reassigning === char.id && (
                                      <Icon name="loader" size={14} style={{ marginLeft: 4 }} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {userChars.length === 0 && (
                            <div style={styles.noCharsRow}>
                              <span style={styles.noChars}>No characters</span>
                              {/* Add to campaign directly */}
                              <select
                                onChange={async (e) => {
                                  if (e.target.value) {
                                    await ensureCampaignMember(e.target.value, u.id);
                                    e.target.value = '';
                                    alert(`Added ${u.display_name} to campaign`);
                                  }
                                }}
                                style={styles.selectCampaign}
                              >
                                <option value="">Add to campaign...</option>
                                {campaigns.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Refresh button */}
                  <button onClick={loadData} style={styles.refreshButton}>
                    <Icon name="refresh-cw" size={14} />
                    Refresh Data
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const GOLD = '#d4af37';

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 'var(--spacing-lg)',
  },

  modal: {
    position: 'relative',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: `2px solid ${GOLD}`,
    width: '100%',
    maxWidth: 800,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },

  header: {
    position: 'relative',
    padding: 'var(--spacing-lg)',
    borderBottom: `1px solid var(--color-border)`,
  },

  title: {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    fontSize: 'var(--font-size-xl)',
    fontFamily: 'var(--font-display)',
    color: GOLD,
  },

  closeIconButton: {
    position: 'absolute',
    top: 'var(--spacing-md)',
    right: 'var(--spacing-md)',
    padding: 'var(--spacing-xs)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
  },

  tabBar: {
    display: 'flex',
    gap: 0,
    borderBottom: '1px solid var(--color-border)',
    padding: '0 var(--spacing-lg)',
  },

  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${GOLD}`,
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
  },

  content: {
    flex: 1,
    overflow: 'auto',
    padding: 'var(--spacing-lg)',
  },

  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-xl)',
    color: 'var(--color-text-secondary)',
  },

  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-danger)',
  },

  stats: {
    display: 'flex',
    gap: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-lg)',
    justifyContent: 'center',
  },

  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    minWidth: 100,
  },

  statNumber: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 700,
    color: GOLD,
  },

  statLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },

  userCard: {
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },

  userHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-background-elevated)',
  },

  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },

  userName: {
    fontWeight: 600,
    color: 'var(--color-text)',
  },

  userId: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    fontFamily: 'monospace',
  },

  charCount: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-surface)',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
  },

  charList: {
    padding: 'var(--spacing-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },

  charItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-sm)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-sm)',
    gap: 'var(--spacing-sm)',
    flexWrap: 'wrap',
  },

  charInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },

  charName: {
    fontWeight: 500,
    color: 'var(--color-text)',
  },

  charCampaign: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
  },

  reassignSection: {
    display: 'flex',
    alignItems: 'center',
  },

  select: {
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    minWidth: 120,
  },

  selectCampaign: {
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    minWidth: 150,
  },

  noCharsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
  },

  noChars: {
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    marginTop: 'var(--spacing-lg)',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  closeButton: {
    padding: 'var(--spacing-sm) var(--spacing-lg)',
    backgroundColor: GOLD,
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
